import { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { USER_ROLE_OPTIONS } from "../constants";
import { useUsers } from "../hooks/useUsers";
// AuthContext.jsx is plain JS, so this import is implicitly typed `any`.
import { useAuth } from "../../../contexts/AuthContext";
import { useUpdateMCPConnection } from "../hooks/useUpdateMCPConnection";
import type {
  MCPSharingUserItem,
  MCPUserRole,
} from "../../../services/agent-tools-service";
import { useTranslation } from "react-i18next";

type Props = {
  connectionId: string;
  users: MCPSharingUserItem[];
};

const UserAccessList = ({ connectionId, users }: Props) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const { data: orgUsers = [] } = useUsers();
  const { userProfile, currentUser } = useAuth();
  const updateMutation = useUpdateMCPConnection();

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of orgUsers) map.set(user.id, user.username);
    return map;
  }, [orgUsers]);

  // The signed-in user stays listed so the sharing picture is complete, but
  // their own row is read-only — you can't demote or remove yourself here.
  // Match on email first: the auth profile carries the Keycloak subject id,
  // which is a different uuid from the org member's `user.id`.
  const selfId = useMemo<string | undefined>(() => {
    const email = (userProfile?.email ?? currentUser?.email)?.toLowerCase();
    const byEmail = email
      ? orgUsers.find((user) => user.email?.toLowerCase() === email)
      : undefined;
    return byEmail?.id ?? userProfile?.uid ?? currentUser?.id;
  }, [orgUsers, userProfile, currentUser]);

  const persist = (next: MCPSharingUserItem[]) => {
    updateMutation.mutate({
      id: connectionId,
      payload: { sharing: { scope: "user", items: next } },
    });
  };

  const handleRoleChange = (userId: string, role: MCPUserRole) => {
    persist(users.map((u) => (u.id === userId ? { ...u, role } : u)));
  };

  const handleRemove = (userId: string) => {
    persist(users.filter((u) => u.id !== userId));
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = nameById.get(u.id) ?? u.id;
      return name.toLowerCase().includes(q);
    });
  }, [search, users, nameById]);

  return (
    <div className="mt-3 space-y-2">
      <div className="relative flex items-center">
        <Search className="absolute start-3 size-4 text-muted-foreground" />
        <input
          type="text"
          placeholder={t("mcpPage.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-10 w-full rounded-[8px] border border-border bg-card ps-9 pe-3 text-sm text-foreground outline-hidden placeholder:text-muted-foreground focus:border-primary"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="py-3 text-sm text-muted-foreground">
          {users.length === 0
            ? t("mcpPage.noUsersShared")
            : t("mcpPage.noMatchingUsers")}
        </p>
      ) : (
        <ul className="space-y-2">
          {filtered.map((user) => {
            const isSelf = user.id === selfId;
            return (
              <li key={user.id} className="flex items-center gap-2">
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  <span className="min-w-0 truncate text-sm text-foreground">
                    {nameById.get(user.id) ?? user.id}
                  </span>
                  {isSelf && (
                    <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      {t("mcpPage.you")}
                    </span>
                  )}
                </span>

                <Select
                  value={user.role}
                  onValueChange={(role) =>
                    handleRoleChange(user.id, role as MCPUserRole)
                  }
                  disabled={isSelf || updateMutation.isPending}
                >
                  <SelectTrigger className="h-9 w-[140px] rounded-[8px] border-border bg-card text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {USER_ROLE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {t(`mcpPage.userRoles.${option.value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <button
                  type="button"
                  onClick={() => handleRemove(user.id)}
                  disabled={isSelf || updateMutation.isPending}
                  aria-label={t("mcpPage.actions.remove", {
                    name: nameById.get(user.id) ?? user.id,
                  })}
                  className="rounded-md p-1.5 text-muted-foreground transition-colors hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <X className="size-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default UserAccessList;
